﻿export interface Listener<T extends Event = Event> {
    event: keyof HTMLElementEventMap | TKCustomEventMap;
    element: EventTarget;
    action: (event: T) => void;
}

export default class TKCustomElementFactory extends HTMLElement {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    listeners: Listener<any>[];

    constructor() {
        super();
        this.listeners = [];
    }

    connectedCallback(): void {}

    /**
     * If the custom element is getting removed of the dom, all listeners
     * are getting removed of the element.
     */
    disconnectedCallback(): void {
        this.listeners.forEach((item) => {
            item.element.removeEventListener(item.event, item.action);
        });
    }

    /**
     * Here you can push a listener object directly to the listener array
     *
     * @param {Listener} {}
     */
    pushListener<T extends Event>({ event, element, action }: Listener<T>): void {
        const existing = this.listeners.find((listener) => listener.element === element && listener.event === event);

        if (existing) {
            existing.element.removeEventListener(existing.event, existing.action);
            this.listeners = this.listeners.filter(
                (listener) => !(listener.element === element && listener.event === event),
            );
        }

        this.listeners.push({ event, element, action });
        element.addEventListener(event, action as EventListener);
    }

    /**
     * Removes the given element from the listener array and removes the
     * EventListener from the element and returns the listener object.
     * If the element was not found, undefined is returned.
     *
     * @param {Element} element
     * @returns {Listener | undefined}
     */
    removeListener(element: EventTarget): Listener | undefined {
        const foundElement = this.listeners.find((item) => item.element === element);
        if (!foundElement) return;
        foundElement?.element.removeEventListener(foundElement.event, foundElement.action);
        const newList = this.listeners.filter((item) => item.element !== element);
        this.listeners = newList;
        return foundElement;
    }

    /**
     * Removes all registered event listeners that were previously added
     * using the `pushListener()` method. This ensures that no dangling
     * or duplicate event handlers remain active on DOM elements.
     *
     * This method should be called before re-rendering or updating the DOM
     * to avoid memory leaks and unintended behavior caused by multiple bindings.
     *
     * @example
     * this.clearListeners();
     * this.render(); // safe to re-bind new listeners after rendering
     */
    clearListeners(): void {
        this.listeners.forEach(({ element, event, action }) => {
            element.removeEventListener(event, action);
        });
        this.listeners = [];
    }

    /**
     * Retrieves a data attribute from the element with the specified name.
     * If the attribute is not present, the provided default value is returned.
     *
     * @protected
     * @param {string} name - The name of the data attribute (without the `data-tk-` prefix).
     * @param {string} defaultValue - The default value to return if the attribute is not found.
     * @returns {string} - The value of the data attribute or the default value.
     */
    getDataAttribute(name: string, defaultValue: string): string {
        return this.getAttribute(`data-tk-${name}`) || defaultValue;
    }
}
